'use client';

// Next.js implementation of the @racefriends/shared nav abstraction.
import React from 'react';
import NextLink from 'next/link';
import NextImage from 'next/image';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import type {
  NavAdapter,
  Nav,
  AppLinkProps,
  AppImageProps,
  QueryParamSetter,
} from '@racefriends/shared/nav';
import { isImageProxyUrl } from '@racefriends/shared/lib/image-proxy';

function useNavImpl(): Nav {
  const router = useRouter();
  return React.useMemo(
    () => ({
      push: (to: string) => router.push(to),
      replace: (to: string) => router.replace(to),
      back: () => router.back(),
    }),
    [router],
  );
}

function useQueryParamImpl(
  key: string,
): [string | null, QueryParamSetter] {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const value = searchParams.get(key);
  const setValue: QueryParamSetter = (next, opts) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next === null) {
      params.delete(key);
    } else {
      params.set(key, next);
    }
    const query = params.toString();
    const url = query ? `${pathname}?${query}` : pathname;
    if (opts?.replace) {
      router.replace(url);
    } else {
      router.push(url);
    }
  };
  return [value, setValue];
}

function Link({ href, children, ...rest }: AppLinkProps) {
  return (
    <NextLink href={href} {...rest}>
      {children}
    </NextLink>
  );
}

function Image({ src, alt, ...rest }: AppImageProps) {
  // A Flyimg URL is already resized and re-compressed. Sending it through
  // Vercel's optimizer only adds a hop, a billable transformation, and a second
  // format negotiation on top of the one Flyimg already did.
  return (
    <NextImage
      src={src}
      alt={alt}
      unoptimized={isImageProxyUrl(src)}
      {...rest}
    />
  );
}

export const webNavAdapter: NavAdapter = {
  useNavImpl,
  useQueryParamImpl,
  Link,
  Image,
};
